import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm({ name: "", email: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section
      id="contact"
      className="relative bg-brand-ink text-brand-cream py-24 md:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-brand-yellow" />
              <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-brand-yellow">
                {t.contact.label}
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              {t.contact.heading}
            </h2>
            <p className="mt-6 font-sans text-base text-brand-cream/60 max-w-md">
              {t.contact.sub}
            </p>

            <a
              href="https://wa.me/6287878731755"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 px-6 py-3 border border-brand-yellow text-brand-yellow hover:bg-brand-yellow hover:text-brand-ink font-sans text-sm tracking-wider transition-colors"
              data-testid="whatsapp-link"
            >
              <MessageCircle className="w-4 h-4" />
              {t.contact.whatsapp}
            </a>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={onSubmit}
            className="col-span-12 md:col-span-7 md:pl-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <Field
                label={t.contact.name}
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid="contact-name"
                required
              />
              <Field
                label={t.contact.email}
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="contact-email"
                required
              />
              <div className="md:col-span-2">
                <Field
                  label={t.contact.phone}
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  testid="contact-phone"
                />
              </div>
              <div className="md:col-span-2">
                <Field
                  label={t.contact.message}
                  textarea
                  value={form.message}
                  onChange={(v) => setForm({ ...form, message: v })}
                  testid="contact-message"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-brand-red text-brand-cream hover:bg-brand-yellow hover:text-brand-ink font-sans text-sm tracking-[0.2em] uppercase transition-colors group"
              data-testid="contact-submit"
            >
              {t.contact.submit}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>

            {sent && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 font-sans text-sm text-brand-yellow"
                data-testid="contact-success"
              >
                {t.contact.sent}
              </motion.p>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", textarea, required, testid }) => {
  const [focused, setFocused] = useState(false);
  const active = focused || value;
  return (
    <div className="relative py-4 border-b border-brand-cream/20 focus-within:border-brand-yellow transition-colors">
      <label
        className={`absolute left-0 font-sans transition-all duration-300 pointer-events-none ${
          active ? "top-0 text-[10px] tracking-[0.2em] uppercase text-brand-yellow" : "top-4 text-sm text-brand-cream/50"
        }`}
      >
        {label}
        {required && active && <span className="text-brand-red ml-1">*</span>}
      </label>
      {textarea ? (
        <textarea
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={3}
          data-testid={testid}
          className="w-full bg-transparent outline-none font-sans text-brand-cream pt-5 resize-none"
        />
      ) : (
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          data-testid={testid}
          className="w-full bg-transparent outline-none font-sans text-brand-cream pt-5"
        />
      )}
    </div>
  );
};
